/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/QaNotesDisclosure.tsx
 * FILE_ID: JOBHUB-QA-NOTES-DISCLOSURE-001
 * PURPOSE: Existing notes always visible; Notes (n) toggles ONLY the composer.
 * WHY: Hiding written notes behind the button made evidence invisible in
 *      Run/ITP (operator regression report, 2026-09-16). Notes are evidence:
 *      the list always renders (with its own collapse chevron, default
 *      expanded); the space-saving toggle is for the empty composer only.
 */
import { ChevronDown, MessageSquare } from 'lucide-react'
import { useState } from 'react'
import { QaNoteThread, type QaNote } from '@quotech/ui'

export function QaNotesDisclosure({
  itemTitle,
  notes,
  onAdd,
  onDelete,
  emptyLabel,
  draftKey,
  className,
}: {
  itemTitle: string
  notes: QaNote[]
  onAdd: (text: string) => void | Promise<void>
  onDelete?: (noteId: string) => void | Promise<void>
  emptyLabel?: string
  draftKey?: string
  className?: string
}) {
  const [composerOpen, setComposerOpen] = useState(false)
  const [notesCollapsed, setNotesCollapsed] = useState(false)
  const hasNotes = notes.length > 0
  return (
    <div className={className} data-testid="qa-notes-disclosure">
      <div className="mt-2 flex items-center gap-2">
        <button
          type="button"
          onClick={() => setComposerOpen((current) => !current)}
          className="flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-foreground"
          aria-expanded={composerOpen}
          aria-label={`Notes for ${itemTitle}`}
        >
          <MessageSquare className="h-3.5 w-3.5" aria-hidden />
          Notes{hasNotes ? ` (${notes.length})` : ''}
        </button>
        {hasNotes && (
          <button
            type="button"
            onClick={() => setNotesCollapsed((current) => !current)}
            className="flex items-center text-muted-foreground hover:text-foreground"
            aria-expanded={!notesCollapsed}
            aria-label={notesCollapsed ? `Show notes for ${itemTitle}` : `Collapse notes for ${itemTitle}`}
          >
            <ChevronDown
              className={`h-3.5 w-3.5 transition-transform ${notesCollapsed ? '-rotate-90' : ''}`}
              aria-hidden
            />
          </button>
        )}
      </div>
      {hasNotes ? (
        // The thread stays MOUNTED so collapsing the composer commits a
        // non-empty draft (showComposer flip) instead of losing it.
        <QaNoteThread
          className="mt-2"
          notes={notesCollapsed ? [] : notes}
          onAdd={onAdd}
          onDelete={onDelete}
          emptyLabel={emptyLabel}
          draftKey={draftKey}
          commitOnUnmount
          showComposer={composerOpen}
        />
      ) : (
        composerOpen && (
          <QaNoteThread
            className="mt-2"
            notes={notes}
            onAdd={onAdd}
            onDelete={onDelete}
            emptyLabel={emptyLabel}
            draftKey={draftKey}
            commitOnUnmount
          />
        )
      )}
    </div>
  )
}
